import React, { useState, useEffect } from 'react';
import { Activity, ActivityEditorProps } from '../types';
import { ActivityInfo } from '../ActivityCommon';
import ActivityHeaderEditor from './ActivityHeaderEditor';
import toast from 'react-hot-toast';

// Exercise Editor Component
const ExerciseEditor: React.FC<ActivityEditorProps> = ({ activity, onActivityUpdate }) => {
  const [exerciseContent, setExerciseContent] = useState('');
  const [exerciseInstructions, setExerciseInstructions] = useState('');
  const [exerciseSubmissionType, setExerciseSubmissionType] = useState('text');
  const [exerciseAttachmentRequired, setExerciseAttachmentRequired] = useState(false);
  const [exerciseTimeLimit, setExerciseTimeLimit] = useState('');
  const [points, setPoints] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState(activity.title);
  const [description, setDescription] = useState(activity.description || '');

  // Initialize fields from activity when component mounts or activity changes
  useEffect(() => {
    if (activity.content) {
      setExerciseContent(activity.content);
    }
    if (activity.instructions) {
      setExerciseInstructions(activity.instructions);
    }
    if (activity.submission_type) {
      setExerciseSubmissionType(activity.submission_type);
    }
    if (activity.attachment_required !== undefined) {
      setExerciseAttachmentRequired(activity.attachment_required);
    }
    if (activity.time_limit) {
      setExerciseTimeLimit(activity.time_limit);
    }
    if (activity.points) {
      setPoints(activity.points.toString());
    }
    if (activity.due_date) {
      setDueDate(activity.due_date);
    }
    setTitle(activity.title);
    setDescription(activity.description || '');
  }, [activity]);

  // Handle AI generated content from parent component
  useEffect(() => {
    const handleAiContent = (event: CustomEvent) => {
      if (event.detail && event.detail.activityId === activity.activity_id) {
        handleAiGeneratedContent(event.detail.content);
      }
    };
    
    window.addEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    
    return () => {
      window.removeEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    };
  }, [activity.activity_id]);

  // Handle AI generated content
  const handleAiGeneratedContent = (generatedContent: any) => {
    console.log('AI generated exercise content:', generatedContent);
    
    if (generatedContent.title) {
      setTitle(generatedContent.title);
    }
    
    if (generatedContent.description) {
      setDescription(generatedContent.description);
    }
    
    if (generatedContent.description) {
      setExerciseInstructions(generatedContent.description);
    }
    
    if (generatedContent.steps && Array.isArray(generatedContent.steps)) {
      setExerciseContent(generatedContent.steps.join('\n\n'));
    }
    
    if (generatedContent.expected_outcome) {
      setExerciseContent(prev => prev + '\n\nExpected Outcome:\n' + generatedContent.expected_outcome);
    }
    
    if (generatedContent.duration) {
      setExerciseTimeLimit(generatedContent.duration);
    }
    
    if (generatedContent.raw_response) {
      toast.error('AI generated content could not be automatically applied. Please try again with a more specific prompt.');
    }
  };

  const handleSaveExercise = async () => {
    if (!onActivityUpdate) return;
    
    try {
      setIsSaving(true);
      
      // Prepare updated exercise data
      const updatedActivity = {
        ...activity,
        content: exerciseContent,
        instructions: exerciseInstructions,
        submission_type: exerciseSubmissionType,
        attachment_required: exerciseAttachmentRequired,
        time_limit: exerciseTimeLimit,
        points: points,
        due_date: dueDate,
        title: title,
        description: description
      };
      
      // Save via the provided update handler
      await onActivityUpdate(updatedActivity);
      
      toast.success('Exercise saved successfully!');
    } catch (error) {
      console.error('Error saving exercise:', error);
      toast.error('Error saving exercise. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Activity Header */}
      <ActivityHeaderEditor
        title={title}
        description={description}
        activityType="Exercise"
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        titlePlaceholder="Exercise Title"
        descriptionPlaceholder="Exercise Description"
      />
      
      <div className="mt-4">
        <h4 className="text-md font-medium mb-2">Exercise Details</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
            <textarea 
              className="w-full h-24 border border-gray-300 rounded-md p-2" 
              placeholder="Enter exercise instructions..."
              value={exerciseInstructions}
              onChange={(e) => setExerciseInstructions(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea 
              className="w-full h-40 border border-gray-300 rounded-md p-2" 
              placeholder="Enter exercise content, problems, or questions..."
              value={exerciseContent}
              onChange={(e) => setExerciseContent(e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Submission Type</label>
              <select 
                className="w-full border border-gray-300 rounded-md p-2"
                value={exerciseSubmissionType}
                onChange={(e) => setExerciseSubmissionType(e.target.value)}
              >
                <option value="text">Text Response</option>
                <option value="file">File Upload</option>
                <option value="both">Text and File</option>
                <option value="code">Code Submission</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Limit (minutes)</label>
              <input 
                type="number" 
                className="w-full border border-gray-300 rounded-md p-2"
                placeholder="Leave empty for no limit"
                value={exerciseTimeLimit}
                onChange={(e) => setExerciseTimeLimit(e.target.value)}
                min="0"
              />
            </div>
          </div>
          
          {exerciseSubmissionType === 'file' || exerciseSubmissionType === 'both' ? (
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="attachmentRequired" 
                checked={exerciseAttachmentRequired}
                onChange={(e) => setExerciseAttachmentRequired(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="attachmentRequired" className="ml-2 block text-sm text-gray-700">
                File attachment is required
              </label>
            </div>
          ) : null}
          
          <div className="pt-4 border-t border-gray-200">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Additional Settings</h5>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                <input 
                  type="number" 
                  className="w-full border border-gray-300 rounded-md p-2"
                  placeholder="Points for this exercise"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input 
                  type="date" 
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={handleSaveExercise}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Exercise'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseEditor; 